import { useMemo, useCallback } from 'react'

import {
  useQueryGetAllBookingInsurances,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { type ParameterID } from '@flecto/types'

type UseBookingInsuranceInfoOptions = {
  bookingId?: ParameterID
}

export function useBookingInsuranceInfo({
  bookingId,
}: UseBookingInsuranceInfoOptions) {
  const { data: user } = useQueryGetUserLogged()

  const { data: booking } = useQueryGetBookingById({
    bookingId,
  })

  const { data: bookingInsurances } = useQueryGetAllBookingInsurances({
    bookingId,
  })

  const isBookingCoveredByInsurance = useMemo(() => {
    if (!booking?.insured) return false
    const today = new Date()
    const end = new Date(booking.end_at)
    return end >= today
  }, [booking])

  const isTodayBetweenBookingDates = useMemo(() => {
    if (!booking?.insured) return false
    const today = new Date()
    const start = new Date(booking.start_at)
    const end = new Date(booking.end_at)
    return (today >= start && today <= end) || today <= start
  }, [booking])

  const isItemInsured = useCallback(
    (itemId: string) => {
      const hasInsurance = bookingInsurances?.insurance?.find(
        (item) => item.id === itemId && item.status === 'active'
      )
      return Boolean(hasInsurance)
    },
    [bookingInsurances]
  )

  const getItemInsuranceDetails = useCallback(
    (itemId: string) => {
      return bookingInsurances?.insurance?.find(
        (item) => item.id === itemId && item.status === 'active'
      )
    },
    [bookingInsurances]
  )

  const isEndAfterToday = useMemo(() => {
    const today = new Date()
    const end = new Date(booking?.end_at ?? 0)
    return end >= today
  }, [booking])

  const insuranceDetails = useMemo(() => {
    if (bookingInsurances?.insurance?.length) {
      const today = new Date()
      let mostRecent = new Date((bookingInsurances.insurance ?? [])[0].end_at || '')
      for (const item of bookingInsurances.insurance ?? []) {
        if (new Date(item.end_at || '') > new Date(mostRecent || '')) {
          mostRecent = new Date(item.end_at || '')
        }
      }
      if (mostRecent <= today) {
        return { date: mostRecent, isExpired: true }
      }
    }
    return { date: null, isExpired: false }
  }, [bookingInsurances])

  const isBookingInsuranceEnabled = useMemo(() => booking?.insured, [booking])

  return {
    isAccountInsuranceEnabled: user?.meta.insured,
    isBookingInsuranceEnabled,
    isBookingCoveredByInsurance,
    isTodayBetweenBookingDates,
    isEndAfterToday,
    isItemInsured,
    insuranceDetails,
    getItemInsuranceDetails,
  }
}
